import React, { useCallback, useContext, useState } from "react";
import { ActionButton, MenuItemType } from "@adsk/alloy-react-menu";
import Theme from "@adsk/alloy-react-theme";
import Button from "@adsk/alloy-react-button";
import { useTranslations } from "@adsk/acc-issues-translations";
import { IssueBulkDeleteConfirmation } from "./IssueBulkDeleteConfirmation";
import { issueLogContext } from "../IssuesTab/IssueLog/issueLogContext";
import { IssueBulkDeleteConfirmationOpenedFrom } from "./IssueBulkDeleteConfirmationOpenedFrom";
import { trackBulkEditDeleteClickEvent } from "../utils/analytics/issueDeletionTracking";
import { ExportIcon, TrashCanIcon } from "@adsk/alloy-react-icon";

enum ISSUES_BULK_EDIT_MENU_OPTIONS {
  DELETE = "issues-bulk-delete",
  EXPORT = "export",
}

export const IssueBulkActionDropdown = ({
  issueUids,
  hasDeletePermission = true,
  onBeginDeletion,
}: {
  issueUids: string[];
  hasDeletePermission: boolean;
  onBeginDeletion: () => void;
}) => {
  const selectedCount = issueUids.length;
  const { T } = useTranslations();
  const exportLabel = T.translate("reports.exportButton.exportSelected", {
    numberOfIssueTableSelectedIds: selectedCount,
  });
  const deleteLabel = T.translate("issueTable.bulkDelete", {
    selectedCount,
  });
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);

  // this component is dependent on the issue log context, for now
  const { toggleExportPanel } = useContext(issueLogContext);

  const handleActionButtonClick = useCallback(
    action => {
      if (!action || !action.length) {
        return;
      }
      if (action[0] === ISSUES_BULK_EDIT_MENU_OPTIONS.DELETE) {
        setIsDeleteConfirmationModalOpen(true);
        trackBulkEditDeleteClickEvent({ selectedCount });
      } else if (action[0] === ISSUES_BULK_EDIT_MENU_OPTIONS.EXPORT) {
        toggleExportPanel();
      }
    },
    [toggleExportPanel, selectedCount]
  );

  return (
    <>
      <ActionButton
        horizontal={false}
        options={[
          ...(hasDeletePermission
            ? [
                {
                  key: ISSUES_BULK_EDIT_MENU_OPTIONS.DELETE,
                  label: deleteLabel,
                  renderIcon: p => <TrashCanIcon {...p} />,
                } as MenuItemType<ISSUES_BULK_EDIT_MENU_OPTIONS>,
              ]
            : []),
          {
            key: ISSUES_BULK_EDIT_MENU_OPTIONS.EXPORT,
            label: exportLabel,
            renderIcon: p => <ExportIcon {...p} />,
          },
        ]}
        variant={Button.VARIANTS.TERTIARY}
        style={{
          color: Theme.colors.charcoal700,
          marginLeft: 4,
          backgroundColor: "transparent", // override ActionButton setting white background
        }}
        onChange={handleActionButtonClick}
      />
      <IssueBulkDeleteConfirmation
        isOpen={isDeleteConfirmationModalOpen}
        issueUids={issueUids}
        onClose={() => {
          setIsDeleteConfirmationModalOpen(false);
        }}
        onBeginDeletion={onBeginDeletion}
        openedFrom={IssueBulkDeleteConfirmationOpenedFrom.BulkEditPanel}
      />
    </>
  );
};
